
import type { ReactNode } from "react";
import styles from "./Alert.module.css";
import { InfoIcon } from "../../../icons/InfoIcon";
import { CheckCircleIcon } from "../../../icons/CheckCircleIcon";
import { AlertTriangleIcon } from "../../../icons/AlertTriangleIcon";
import { AlertCircleIcon } from "../../../icons/AlertCircleIcon";
import { XIcon } from "../../../icons/XIcon";

type AlertVariant = "info" | "success" | "warning" | "danger";

const VARIANT_ICONS: Record<AlertVariant, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: AlertTriangleIcon,
  danger: AlertCircleIcon,
};

interface AlertProps {
  readonly variant?: AlertVariant;
  readonly title?: string;
  readonly children: ReactNode;
  readonly onDismiss?: () => void;
  readonly className?: string;
}

export function Alert({
  variant = "info",
  title,
  children,
  onDismiss,
  className,
}: AlertProps) {
  const Icon = VARIANT_ICONS[variant];

  return (
    <div role="alert" className={`${styles.alert} ${styles[variant]} ${title ? styles.hasTitle : ""} ${className ?? ""}`}>
      <div className={styles.iconBadge} aria-hidden="true">
        <Icon width={18} height={18} />
      </div>
      <div className={styles.content}>
        {title && <p className={styles.title}>{title}</p>}
        <div className={styles.message}>{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={styles.dismiss}
          aria-label="Dismiss"
        >
          <XIcon width={16} height={16} />
        </button>
      )}
    </div>
  );
}
